import { formatDateTimeWithTimezone, formatDateToMMDDYY } from './date'
import { RequestStatus } from '../types/timeOffRequestDetails'

/** Statuses with no approver activity to report yet; hidden from CurrentStatus / RequestHistory. */
export const ignoredStatuses = ['SUBMITTED', 'DRAFT']


/** A single labeled value rendered by CurrentStatus / RequestHistory. */
export interface StatusField {
  label: string
  value: string | null | undefined
}

/**
 * Identifies request states that should be treated as retraction denied UI.
 * @param status - Raw status string from API or transformed detail model.
 * @returns True when the status is in the retraction denied family.
 */
export const isRetractionDeniedStatus = (
  status: string | null | undefined
): boolean => {
  const code = status?.toUpperCase().trim() ?? ''
  return (
    code === 'RTDENIED' ||
    code === 'OVERRIDDEN_RTDENIED' ||
    code === 'PAPPROVED_RTDENIED' ||
    code === 'TORPAPPROVEDRETRACTIONDENIED' ||
    code === 'PARTIALLY DENIED'
  )
}

/**
 * Requestor History section visibility gate used by requestor/status/{id}.
 * @param status - Current request status.
 * @param history - Transformed history events for the request.
 * @returns True when the status supports history and events are present.
 */
export const shouldRenderRequestorHistory = (
  _status: string | null | undefined,
  history: unknown[] | null | undefined
): boolean => {
  return (history?.length ?? 0) > 0
}

/**
 * Normalized shape both `RequestStatus` (current status) and `AuditHistoryItem`
 * (history) are adapted into so the field-mapping logic below can be shared.
 */
export interface StatusEntry {
  status: string | null | undefined
  createdTime?: string | null
  comments?: string | null
  denialReason?: string | null
  mgrRequestedRetraction?: string | null
  firstName?: string | null
  lastName?: string | null
  fullName?: string | null
  approverIdentifier?: string | null
  updatedBy?: string | null
  torRank?: number | null
  createdBy?: string | null
  manualReqRcvdDate?: string | null
}

/** Adapts a `statuses[]` entry (the latest/current status record) into the shared `StatusEntry` shape. */
export const toRequestStatusEntry = (status: RequestStatus): StatusEntry => ({
  status: status.status,
  createdTime: status.createdTime,
  comments: status.comments,
  denialReason: status.denialReason,
  mgrRequestedRetraction: status.mgrRequestedRetraction,
  firstName: status.firstName,
  lastName: status.lastName,
  fullName: status.fullName,
  approverIdentifier: status.approverNUID,
  updatedBy: status.updatedBy,
  torRank: status.torRank,
  createdBy: status.createdBy,
  manualReqRcvdDate: status.manualReqRcvdDate,
})

// A status record can be revised after creation (e.g. hold released); prefer that revision time
// wherever the current/latest status is shown (below-action-buttons summary, read-only status
// panel), falling back to createdTime when there is no update yet
export const toLatestStatusEntry = (status: RequestStatus): StatusEntry => ({
  ...toRequestStatusEntry(status),
  createdTime: status.updatedTime || status.createdTime,
})

/**
 * Convenience wrapper around `getCurrentStatusFields` for the approver's latest `statuses[0]`
 * record; returns no fields for a missing or ignored (first-submission) status.
 * @param status - Latest status record, or undefined when the request has no status yet.
 * @param partialRefId - Linked request ID shown for partial approval/retraction statuses.
 * @returns The labeled fields to render, or an empty array when there is nothing to show.
 */
export const getLatestStatusFields = (
  status: RequestStatus | undefined,
  partialRefId: string | undefined | null,
  manualReqRcvdDate?: string | null,
  isVacationBid = false,
  ownerNuid?: string | null
): StatusField[] => {
  if (!status || ignoredStatuses.includes(status.status?.toUpperCase() ?? '')) {
    return []
  }
  return getCurrentStatusFields(
    {
      ...toLatestStatusEntry(status),
      manualReqRcvdDate,
    },
    partialRefId,
    isVacationBid,
    ownerNuid
  )
}

const normalizeNuid = (value?: string | null): string => (value ?? '').trim().toLowerCase()

/**
 * Resolves the display name for a status/history entry's actor, prefixed with
 * "Requestor Proxy" when the actor's nuid differs from the request owner's
 * (for statuses where that actor can be the employee or a proxy on their behalf).
 * @param entry - Normalized status/history entry.
 * @param ownerNuid - The request owner's nuid, used to detect a proxy actor.
 */
export const getFullName = (entry: StatusEntry, ownerNuid?: string | null) => {
  const name =
    entry.fullName ||
    (entry.firstName && entry.lastName
      ? `${entry.firstName} ${entry.lastName}`.trim()
      : entry.firstName) ||
    entry.approverIdentifier ||
    'System'

    const proxyStatuses = [
    'SUBMITTED',
    'RESUBMITTED',
    'WITHDRAWN',
    'RTSUBMITTED',
    'PRTSUBMITTED',
    'APPROVED_PRTSUBMITTED'
  ]

  const isProxyEligibleStatus = proxyStatuses.includes(entry.status?.toUpperCase() ?? '')

  // Only label the actor a proxy when they differ from the request owner —
  // not gated on manualReqRcvdDate, since a proxy can resubmit/withdraw a
  // request the employee originally submitted themselves, and vice versa.
  const actorNuid = normalizeNuid(entry.updatedBy)
  const isRequestorProxy =
    isProxyEligibleStatus &&
    !!actorNuid &&
    !!normalizeNuid(ownerNuid) &&
    actorNuid !== normalizeNuid(ownerNuid)

  if (isRequestorProxy) {
    return `Requestor Proxy ${withNuid(name, entry.updatedBy)}`
  }

  // Submitted/Resubmitted/Withdrawn/Retraction-submitted "By" fields always show the
  // nuid, proxy or not; other getFullName consumers (Released By, Returned By, Hold
  // Placed By) intentionally don't.
  return isProxyEligibleStatus ? withNuid(name, entry.updatedBy) : name
}

// Appends "(nuid)" unless it's missing or the name already fell back to that same nuid
const withNuid = (name: string, nuid?: string | null): string => {
  const trimmedNuid = nuid?.trim()
  return trimmedNuid && name !== trimmedNuid ? `${name} (${trimmedNuid})` : name
}

// Every "Authorized Approver" field's NUID comes from entry.updatedBy, which the
// StatusEntry adapters already resolve to updatedBy (current status) / lastUpdatedBy (audit history)
const getAuthorizedApproverValue = (entry: StatusEntry): string =>
  withNuid(getFullName(entry), entry.updatedBy)

// Batch/system actors can appear as TortSystem or TORT_BATCH depending on source.
const isSystemActor = (value?: string | null): boolean => {
  if (!value) return false
  const normalized = value.toLowerCase().trim()
  return normalized.includes('tortsystem') || normalized === 'tort_batch'
}

const normalizeSystemActorDisplay = (value?: string | null): string | undefined =>
  isSystemActor(value) ? 'System' : value ?? undefined

// Only the placing of a hold can originate from the system; releases are always a human approver
const resolveHoldActorName = (entry: StatusEntry): string =>
  isSystemActor(entry.updatedBy) ? 'System' : getFullName(entry)

const formatStatusDate = (date?: string | null): string =>
  date ? formatDateTimeWithTimezone(date) : ''

// Comments/denial reasons may arrive URI-encoded; fall back to the raw text if decoding fails
export const decodeComment = (s: string | null | undefined): string | undefined => {
  if (!s) return undefined
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

const getHoldStatusFields = (
  entry: StatusEntry,
  released = false,
  includeRankOnRelease = false,
  isVacationBid = false
) => [
  {
    label: released ? 'Hold Released On' : 'Hold Placed On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: released ? 'Released By' : 'Hold Placed By',
    value: released
      ? normalizeSystemActorDisplay(getFullName(entry))
      : resolveHoldActorName(entry),
  },
  ...(((!released || includeRankOnRelease) && (entry.torRank != null || isVacationBid))
    ? [{ label: 'Request Rank', value: entry.torRank == null ? 'NA' : String(entry.torRank) }]
    : []),
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
]

const getApprovalStatusFields = (
  entry: StatusEntry,
  label: string = 'Approved On',
  includeRank = false,
  isVacationBid = false,
  showRankWhenUnavailable = false
) => [
  {
    label,
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Authorized Approver',
    value: getAuthorizedApproverValue(entry),
  },
  // Rank is shown only for status flows that explicitly opt into it.
  ...(includeRank && (entry.torRank != null || isVacationBid || showRankWhenUnavailable)
    ? [{ label: 'Request Rank', value: entry.torRank == null ? 'NA' : String(entry.torRank) }]
    : []),
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
]

const getDeniedStatusFields = (
  entry: StatusEntry,
  dateLabel = 'Denied On',
  includeRank = false,
  isVacationBid = false
) => [
  {
    label: dateLabel,
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Authorized Approver',
    value: getAuthorizedApproverValue(entry),
  },
  ...(includeRank && (entry.torRank != null || isVacationBid)
    ? [{ label: 'Request Rank', value: entry.torRank == null ? 'NA' : String(entry.torRank) }]
    : []),
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
  {
    label: 'Denial Reason',
    value: decodeComment(entry.denialReason),
  },
]

const getPartialRetractionSubmittedStatusFields = (
  entry: StatusEntry,
  ownerNuid?: string | null
) => [
  {
    label: 'Partial Retraction Submitted On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Submitted By',
    value: getFullName(entry, ownerNuid),
  },
  ...(entry.torRank == null
    ? []
    : [{ label: 'Request Rank', value: String(entry.torRank) }]),
  {
    label: 'Employee Comments Regarding Retraction',
    value: decodeComment(entry.comments),
  },
  ...(entry.mgrRequestedRetraction === 'Y'
    ? [
        {
          label: 'Approver Requested Retraction',
          value: 'Approver asked for this request to be retracted.',
        },
      ]
    : []),
]

// Original request already approved; a subsequent partial retraction is now pending approval
const getApprovedPartialRetractionSubmittedStatusFields = (
  entry: StatusEntry,
  partialRefId: string | undefined | null,
  ownerNuid?: string | null
) => [
  {
    label: 'Approved On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Submitted By',
    value: getFullName(entry, ownerNuid),
  },
  {
    label: 'Partial Retraction Reference ID',
    value: partialRefId,
  },
  ...(entry.torRank == null
    ? []
    : [{ label: 'Request Rank', value: String(entry.torRank) }]),
  {
    label: 'Employee Comments Regarding Retraction',
    value: decodeComment(entry.comments),
  },
  ...(entry.mgrRequestedRetraction === 'Y'
    ? [
        {
          label: 'Approver Requested Retraction',
          value: 'Approver asked for this request to be retracted.',
        },
      ]
    : []),
]

const getPartialRetractionDeniedStatusFields = (entry: StatusEntry) => [
  {
    label: 'Partial Retraction Denied On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Authorized Approver',
    value: getAuthorizedApproverValue(entry),
  },
  ...(entry.torRank == null
    ? []
    : [{ label: 'Request Rank', value: String(entry.torRank) }]),
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
  {
    label: 'Denial Reason',
    value: decodeComment(entry.denialReason),
  },
]

const getPartiallyApprovedStatusFields = (
  entry: StatusEntry,
  partialRefId: string | undefined | null
) => [
  {
    label: 'Partially Approved On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Authorized Approver',
    value: getAuthorizedApproverValue(entry),
  },
  {
    label: 'Denied Time Off Request ID',
    value: partialRefId,
  },
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
]

const getRetractionSubmittedStatusFields = (
  entry: StatusEntry,
  isVacationBid = false,
  ownerNuid?: string | null
) => [
  {
    label: 'Retraction Submitted On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Submitted By',
    value: getFullName(entry, ownerNuid),
  },
  {
    label: 'Employee Comments Regarding Retraction',
    value: decodeComment(entry.comments),
  },
  ...(entry.torRank != null || isVacationBid
    ? [{ label: 'Request Rank', value: entry.torRank == null ? 'NA' : String(entry.torRank) }]
    : []),
  ...(entry.mgrRequestedRetraction === 'Y'
    ? [
        {
          label: 'Approver Requested Retraction',
          value: 'Approver asked for this request to be retracted.',
        },
      ]
    : []),
]

const getResubmittedStatusFields = (
  entry: StatusEntry,
  ownerNuid?: string | null
) => [
  {
    label: 'Resubmitted On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Resubmitted By',
    value: getFullName(entry, ownerNuid),
  },
  {
    label: 'Employee Comments',
    value: decodeComment(entry.comments),
  },
  {
    label: 'Manual Request Received Date',
    value: entry.manualReqRcvdDate
      ? formatDateToMMDDYY(entry.manualReqRcvdDate)
      : undefined,
  },
]

const getWithdrawnStatusFields = (
  entry: StatusEntry,
  ownerNuid?: string | null
) => [
  {
    label: 'Withdrawn On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Withdrawn By',
    value: getFullName(entry, ownerNuid),
  },
  ...(entry.torRank == null
    ? []
    : [{ label: 'Request Rank', value: String(entry.torRank) }]),
]

const getReturnedStatusFields = (entry: StatusEntry) => [
  {
    label: 'Returned On',
    value: formatStatusDate(entry.createdTime),
  },
  {
    label: 'Returned By',
    value: getFullName(entry),
  },
  {
    label: 'Approver Comments',
    value: decodeComment(entry.comments),
  },
]

const getDefaultStatusFields = (entry: StatusEntry) => [
  {
    label: 'Updated On',
    value: formatStatusDate(entry.createdTime),
  },
]

// Appends the linked original/partial request ID field shared by several partial-status cases
const withRefId = (
  fields: StatusField[],
  label: string,
  refId: string | undefined | null
) => [...fields, { label, value: refId }]

/**
 * Maps a normalized status entry to the labeled fields shown for it, keyed by
 * status code. Shared by CurrentStatus (latest status) and RequestHistory
 * (each audit entry) so the mapping only lives in one place.
 * @param entry - Normalized status/history entry.
 * @param partialRefId - Linked request ID shown for partial approval/retraction statuses.
 * @param isVacationBid - Whether the request contains a Vacation Bid allocation.
 * @param ownerNuid - The request owner's nuid, used to tell an actual proxy actor apart from the owner acting on their own request.
 * @returns The ordered list of labeled fields to render for this status.
 */
export const getCurrentStatusFields = (
  entry: StatusEntry,
  partialRefId: string | undefined | null,
  isVacationBid = false,
  ownerNuid?: string | null
): StatusField[] => {
  const statusCode = entry.status?.toUpperCase()

  switch (statusCode) {
    case 'SUBMITTED_ONHOLD':
    case 'RTSUBMITTED_ONHOLD':
    case 'RESUBMITTED_ONHOLD':
      return getHoldStatusFields(entry, false, false, isVacationBid)

    case 'SUBMITTED_RELEASED':
    case 'RTSUBMITTED_RELEASED':
      return getHoldStatusFields(entry, true, false, isVacationBid)

    case 'RESUBMITTED_RELEASED':
    case 'RESUBMITTED_RELEASE':
      return [
        {
          label: 'Hold Released On',
          value: formatStatusDate(entry.createdTime),
        },
        {
          label: 'Released By',
          value: normalizeSystemActorDisplay(getFullName(entry)),
        },
        ...(entry.torRank == null && !isVacationBid
          ? []
          : [{ label: 'Request Rank', value: entry.torRank == null ? 'NA' : String(entry.torRank) }]),
        {
          label: 'Approver Comments',
          value: decodeComment(entry.comments),
        },
      ]

    case 'PRTSUBMITTED_ONHOLD':
      return withRefId(
        getHoldStatusFields(entry, false, false, isVacationBid),
        'Original Time Off Request ID',
        partialRefId
      )

    case 'PRTSUBMITTED_RELEASED':
      return withRefId(
        getHoldStatusFields(entry, true, true, isVacationBid),
        'Original Time Off Request ID',
        partialRefId
      )

    case 'APPROVED':
      return getApprovalStatusFields(entry)

    case 'OVERRIDDEN':
      return getApprovalStatusFields(entry, 'Overridden On', true)

    case 'RETRACTED':
      return getApprovalStatusFields(entry, 'Retraction Approved On', true)

    case 'RTDENIED_OVERRIDDEN':
      return getApprovalStatusFields(entry, 'Retraction Approved On', true, false, true)

    case 'PRETRACTED':
    case 'PRTDENIED_OVERRIDDEN':
      return withRefId(
        getApprovalStatusFields(entry, 'Partial Retraction Approved On', true),
        'Original Time Off Request ID',
        partialRefId
      )

    case 'DENIED':
      return getDeniedStatusFields(entry)

    case 'RTDENIED':
    case 'OVERRIDDEN_RTDENIED':
    case 'PAPPROVED_RTDENIED':
    case 'TORPAPPROVEDRETRACTIONDENIED':
      return getDeniedStatusFields(entry, 'Retraction Denied On', true, isVacationBid)

    case 'PDENIED':
      return withRefId(
        getDeniedStatusFields(entry, 'Partially Denied On'),
        'Approved Time Off Request ID',
        partialRefId
      )

    case 'PAPPROVED':
      return getPartiallyApprovedStatusFields(entry, partialRefId)

    case 'RTSUBMITTED':
      return getRetractionSubmittedStatusFields(entry, isVacationBid, ownerNuid)

    case 'PRTSUBMITTED':
      return withRefId(
        getPartialRetractionSubmittedStatusFields(entry, ownerNuid),
        'Original Time Off Request ID',
        partialRefId
      )

    case 'APPROVED_PRTSUBMITTED':
      return getApprovedPartialRetractionSubmittedStatusFields(entry, partialRefId, ownerNuid)

    case 'PRTDENIED':
      return withRefId(
        getPartialRetractionDeniedStatusFields(entry),
        'Original Time Off Request ID',
        partialRefId
      )

    case 'RESUBMITTED':
      return getResubmittedStatusFields(entry, ownerNuid)

    case 'WITHDRAWN':
      return getWithdrawnStatusFields(entry, ownerNuid)

    case 'RETURNED':
      return getReturnedStatusFields(entry)

    default:
      return getDefaultStatusFields(entry)
  }
}
